import ErrorBoundary from "@components/error-boundary";
import { BeneficiaryOnboarding } from "@pages/onboarding/beneficiary/beneficiary-onboarding";
import { OnboardingCompleted } from "@pages/onboarding/beneficiary/forms/completed";
import { OnboardingFollowup } from "@pages/onboarding/beneficiary/forms/followup";
import { OnboardingStateMachineProvider } from "@pages/onboarding/beneficiary/machines/machine-provider";
import { WaitingSplash } from "@pages/onboarding/shared/waiting";
import { useFeatureGate, useStatsigClient } from "@statsig/react-bindings";
import { useContact, usePetitions } from "@utils/hooks";
import { PetitionStatus, OnboardingStatusType } from "@codegen/schema";
import { useOnboardingByPetitionIdQuery, useGetPetitionsByUserIdQuery } from "@codegen/index";
import * as changeCase from "change-case";
import * as React from "react";
import {
  Link,
  Route,
  Routes,
  useParams,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  BeneficiaryOnboardingWorkflowProvider,
  useBeneficiaryWorkflowContext,
} from "./provider";

const BeneficiaryOnboardingRoot = () => {
  const { contact, team } = useContact();
  const {
    modules,
    currentModuleIndex,
    currentExhibitIndex,
    initializeContext,
    petition,
    onboarding,
  } = useBeneficiaryWorkflowContext();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const exhibitId = searchParams.get("exhibitId");

  React.useEffect(() => {
    const moduleKey = params["module"];
    if (moduleKey && modules && modules.length) {
      const moduleIndex = moduleKey
        ? modules.findIndex((modArr) => {
            return id
              ? modArr.id === id
              : modArr.type === changeCase.constantCase(moduleKey);
          })
        : modules.findIndex((mod) =>
            ["pending", "in_progress"].includes(mod.status)
          );

      const exhibits = modules[moduleIndex]?.exhibits || [];
      const exhibitIndex = exhibits.findIndex((ex) =>
        exhibitId ? ex.id === exhibitId : -1
      );
      initializeContext({
        moduleIndex: moduleIndex > -1 ? moduleIndex : 0,
        exhibitIndex: exhibitIndex > -1 ? exhibitIndex : 0,
      });
    } else if (!moduleKey) {
      initializeContext({
        moduleIndex: 0,
        exhibitIndex: 0,
      });
    }
  }, [params, initializeContext, modules]);

  if (
    !contact ||
    !team ||
    !modules ||
    !modules.length ||
    typeof currentModuleIndex !== "number" ||
    typeof currentExhibitIndex !== "number" ||
    !petition ||
    !onboarding
  ) {
    return <WaitingSplash />;
  }

  return (
    <ErrorBoundary>
      <OnboardingStateMachineProvider>
        <BeneficiaryOnboarding
          company={team}
          contact={contact}
          modules={modules}
          currentModuleIndex={currentModuleIndex}
          currentExhibitIndex={currentExhibitIndex}
          petition={petition}
          onboarding={onboarding}
        />
      </OnboardingStateMachineProvider>
    </ErrorBoundary>
  );
};

const OnboardingNotFound = () => {
  return (
    <div className="w-screen h-screen flex justify-center items-center">
      <div>
        <h1>:(</h1>
        <p className="mb-3">Page not found</p>
        <Link to="/" className="text-accent hover:underline">
          Back to home
        </Link>
      </div>
    </div>
  );
};

// Hook to check if there's valid beneficiary onboarding
export const useHasValidBeneficiaryOnboarding = () => {
  const { contact } = useContact();
  // Query petitions by user ID (personal context) regardless of current team
  const { data: petitionData, loading: petitionLoading } = useGetPetitionsByUserIdQuery({
    variables: {
      id: contact?.id ?? "",
    },
    skip: !contact?.id,
  });

  const validBeneficiaryPetition = React.useMemo(() => {
    if (!petitionData?.allPetitions?.nodes) return null;

    const petitions = petitionData.allPetitions.nodes.filter((p) => p != null);
    const latestPetition = petitions.find((p) => p?.latestPetition) ?? petitions[0];

    return latestPetition ?? null;
  }, [petitionData]);

  const {
    data: onboardingData,
    loading: onboardingLoading,
  } = useOnboardingByPetitionIdQuery({
    variables: {
      petitionId: validBeneficiaryPetition?.id ?? "",
    },
    skip: !validBeneficiaryPetition?.id,
  });

  const hasActiveBeneficiaryOnboarding = React.useMemo(() => {
    if (!validBeneficiaryPetition || !onboardingData?.allOnboardings?.nodes?.[0]) {
      return false;
    }

    const onboarding = onboardingData.allOnboardings.nodes[0];
    const status = onboarding?.status;

    // Check if onboarding is active (not completed, rejected, or canceled)
    return (
      status === OnboardingStatusType.Pending ||
      status === OnboardingStatusType.InProgress
    );
  }, [validBeneficiaryPetition, onboardingData]);

  return {
    hasActiveBeneficiaryOnboarding,
    loading: petitionLoading || onboardingLoading,
  };
};

export const OnboardingWorkflows: React.FC<{}> = () => {
  const { value } = useFeatureGate("beneficiary-onboarding");
  const { loading, team } = useContact();
  const { client } = useStatsigClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: petitionData, loading: petitionLoading } = usePetitions();
  const { hasActiveBeneficiaryOnboarding, loading: onboardingLoading } = useHasValidBeneficiaryOnboarding();

  React.useEffect(() => {
    client.logEvent(`onboarding_workflows`, "root_view", {
      beneficiaryOnboardingFlag: value.toString(),
      path: location.pathname,
    });
  }, []);

  // Find valid beneficiary onboarding petition
  const validBeneficiaryPetition = petitionData?.latestPetition;

  // Check for employer onboarding as fallback
  const hasEmployerOnboarding = React.useMemo(() => {
    if (!petitionData?.petitions || !team) return false;

    // Check if there are any petitions with PendingDocs status and latestPetition for employer onboarding
    const employerPetitions = petitionData.petitions.filter(
      (p) =>
        p.status === PetitionStatus.PendingDocs &&
        p.latestPetition &&
        // Employer onboarding is for company/team context
        team.type === "team" &&
        team.value
    );

    return employerPetitions.length > 0;
  }, [petitionData, team]);

  // Redirect to employer onboarding if no valid beneficiary onboarding
  React.useEffect(() => {
    // Only redirect if we're not already on the employer onboarding page
    const isOnEmployerOnboarding = location.pathname.startsWith("/onboarding/employer");

    if (
      !loading &&
      !petitionLoading &&
      !onboardingLoading &&
      team &&
      !hasActiveBeneficiaryOnboarding &&
      hasEmployerOnboarding &&
      !isOnEmployerOnboarding
    ) {
      navigate("/onboarding/employer", { replace: true });
    }
  }, [
    loading,
    petitionLoading,
    onboardingLoading,
    team,
    hasActiveBeneficiaryOnboarding,
    hasEmployerOnboarding,
    navigate,
    location.pathname,
  ]);

  if (loading || petitionLoading || onboardingLoading || !team) {
    return <WaitingSplash />;
  }

  // If no valid beneficiary onboarding and no employer onboarding, show waiting
  if (!hasActiveBeneficiaryOnboarding && !hasEmployerOnboarding) {
    return <WaitingSplash />;
  }

  // If no valid beneficiary onboarding but employer onboarding exists, we're redirecting
  if (!hasActiveBeneficiaryOnboarding && hasEmployerOnboarding) {
    return <WaitingSplash />;
  }

  const petitionId = validBeneficiaryPetition?.id;

  if (!petitionId) {
    return <WaitingSplash />;
  }

  return (
    <BeneficiaryOnboardingWorkflowProvider petitionId={petitionId}>
      <Routes>
        {/* <Route path="/employer/:team?" element={<EmployerOnboardingRoot />} /> */}
        {
          <>
            <Route path="/complete" element={<OnboardingCompleted />} />
            <Route path="/:module" element={<BeneficiaryOnboardingRoot />} />
            <Route path="/followup" element={<OnboardingFollowup />} />
            <Route path="/*" element={<BeneficiaryOnboardingRoot />} />
          </>
        }

        {/* <Route path="*" element={<OnboardingNotFound />} /> */}
      </Routes>
    </BeneficiaryOnboardingWorkflowProvider>
  );
};
